import type { ManiaLane } from '@shared/maniaNote';
import {
  HIT_STOP_MAX_MS,
  SCORE_HUD_TARGET_TOP_PAD_PX,
  LANE_GAP_PX,
} from './render/pianoRenderConstants';
import { computeLayout } from './pianoPlayfieldGeometry';
import { PianoSceneRenderer } from './render/pianoDrawScene';
import { TileRenderer } from './render/pianoDrawTiles';
import { PianoFxSystem } from './render/pianoFx';
import { KeyLabelRenderer } from './render/pianoDrawKeyLabels';
import { HudRenderer } from './render/pianoDrawHud';
import {
  LeadInBannerState,
  drawCountdown,
  drawFloatingTexts,
  drawPausedOverlay,
  drawScorePops,
} from './render/pianoDrawOverlays';
import type { PianoRenderInput } from './render/pianoRenderTypes';
import { getRenderQuality, type RenderQuality } from '../rendering/renderQuality';

/**
 * Premium 4-lane renderer with full FX pipeline: hit-line particle
 * bursts, expanding shockwaves, per-lane verdict flashes, +N score
 * pops, screen shake, hit-stop micro-freezes, tile danger pulses,
 * dynamic background bloom, and animated combo HUD. All effects
 * collapse to lightweight static draws under `prefers-reduced-motion`.
 *
 * The session pushes intent (emission specs, flash specs, shake
 * requests) each frame; the renderer owns the visual lifecycle
 * (long-lived particles aging across frames, shockwave expansion,
 * flash decay) so the session doesn't have to thread per-particle
 * physics through React state.
 *
 * Heavy lifting lives in `./render/*` — this class is a thin
 * orchestrator that owns canvas + DPR bookkeeping, the small set of
 * subsystem instances, the per-frame paint order, and the public
 * session-facing surface (`requestHitStop`, `clearLoopBoundFx`,
 * `getLaneHitPoint`, `getScoreHudTarget`).
 */
export class PianoRenderer {
  private ctx: CanvasRenderingContext2D;
  private canvas: HTMLCanvasElement;
  /** Cached devicePixelRatio so we only resize the backing store on change. */
  private lastDpr = 1;
  /** Remaining hit-stop time (seconds). While >0, animation dt is frozen. */
  private hitStopRemaining = 0;
  private reducedMotion = false;
  private quality: RenderQuality;
  private lastFrameAt = performance.now();
  private fx: PianoFxSystem;
  private keyLabels = new KeyLabelRenderer();
  private hud = new HudRenderer();
  private leadInBanner = new LeadInBannerState();
  private tiles = new TileRenderer();
  private scene = new PianoSceneRenderer();

  constructor(canvas: HTMLCanvasElement) {
    this.canvas = canvas;
    const ctx = canvas.getContext('2d');
    if (!ctx) {
      throw new Error('PianoRenderer: 2D context unavailable');
    }
    this.ctx = ctx;
    if (typeof window !== 'undefined' && window.matchMedia) {
      this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    }
    this.quality = getRenderQuality();
    this.fx = new PianoFxSystem(this.reducedMotion, this.quality);
  }

  /**
   * Trigger a brief render-time freeze. Particle / shockwave / flash
   * / shake / score-pop animations hold their current frame for `ms`
   * milliseconds; the engine and audio are NOT affected.
   */
  requestHitStop(ms: number): void {
    if (this.reducedMotion) {
      return;
    }
    const clamped = Math.min(ms, HIT_STOP_MAX_MS);
    this.hitStopRemaining = Math.max(this.hitStopRemaining, clamped / 1000);
  }

  /**
   * Drop renderer-owned FX state on loop boundaries. The session
   * calls this between loops so a residual particle / shockwave
   * from loop N doesn't ghost-render through loop N+1's opening
   * countdown. Also resets the wall-clock dt anchor so the first
   * frame of the new loop doesn't age particles by the gap between
   * loops.
   */
  clearLoopBoundFx(): void {
    this.fx.clear();
    this.hitStopRemaining = 0;
    this.hud.reset();
    this.keyLabels.reset();
    this.leadInBanner.reset();
    this.tiles.reset();
    this.lastFrameAt = performance.now();
  }

  draw(input: PianoRenderInput): void {
    const { width, height, chart, paused } = input;
    if (width <= 0 || height <= 0) {
      return;
    }
    this.ensureBackingStore(width, height);
    const ctx = this.ctx;
    const palette = chart.palette;
    const layout = computeLayout(width, height);

    // ---- Tick animation time (frozen during hit-stop) ----
    const now = performance.now();
    const wallDt = Math.min(0.05, Math.max(0, (now - this.lastFrameAt) / 1000));
    this.lastFrameAt = now;
    let animDt: number;
    if (this.hitStopRemaining > 0) {
      this.hitStopRemaining = Math.max(0, this.hitStopRemaining - wallDt);
      animDt = 0;
    } else {
      animDt = wallDt;
    }

    // ---- Consume incoming specs (spawn long-lived FX entities) ----
    this.fx.consumeEmissions(input.particleEmissions);
    this.fx.consumeShockwaveSpecs(input.shockwaveSpecs);
    this.fx.consumeLaneFlashSpecs(input.laneFlashSpecs);

    // ---- Age long-lived FX ----
    this.fx.advanceAll(animDt);
    this.hud.advance(input.state.combo, animDt);
    this.keyLabels.advance(input.pressedLanes, animDt);
    this.leadInBanner.advance(input.countdown, animDt);

    // ---- Shake offset (applied to whole frame via ctx.translate) ----
    const shakeX = this.reducedMotion ? 0 : (Math.random() * 2 - 1) * input.shake.amount;
    const shakeY = this.reducedMotion ? 0 : (Math.random() * 2 - 1) * input.shake.amount;

    ctx.save();
    ctx.translate(shakeX, shakeY);

    this.scene.drawBackground(ctx, width, height, palette, input.state.combo, this.reducedMotion, this.quality);
    this.scene.drawLaneBackgrounds(ctx, layout, height, palette, input.pressedLanes);
    this.scene.drawLaneFlashes(ctx, this.fx.getLaneFlashes(), layout, height);
    this.tiles.draw(ctx, input, layout, this.reducedMotion, this.quality);
    this.scene.drawHitLine(ctx, layout, palette, input.state.combo, this.reducedMotion, this.quality);
    this.fx.drawShockwaves(ctx);
    this.fx.drawParticles(ctx);
    this.keyLabels.draw(ctx, layout, height, palette, input.pressedLanes, input.keyLabels, this.reducedMotion);
    drawFloatingTexts(
      ctx,
      input,
      layout,
      width,
      height,
      this.fx,
      this.leadInBanner,
      this.reducedMotion,
    );
    drawScorePops(ctx, input.scorePops, this.reducedMotion);
    this.hud.draw(ctx, input, layout, this.reducedMotion, this.quality);

    if (this.leadInBanner.shouldDrawCountdown(input.countdown)) {
      drawCountdown(
        ctx,
        input.countdown,
        width,
        height,
        palette,
        this.leadInBanner.getGoPulseRemaining(),
        this.reducedMotion,
      );
    }
    if (paused) {
      drawPausedOverlay(ctx, width, height, palette);
    }

    ctx.restore();
  }

  /**
   * Resize the canvas backing store to match the current viewport at
   * the device-pixel-ratio so HiDPI screens stay sharp. Only re-
   * allocates when DPR or CSS dimensions actually changed.
   *
   * DPR is clamped by the render-quality tier — uncapped on phones
   * is 9× the fill-rate of a DPR-1 render. The 1.5 cap is the single
   * biggest perf win in this renderer.
   */
  private ensureBackingStore(width: number, height: number): void {
    const rawDpr = window.devicePixelRatio || 1;
    const dpr = Math.min(rawDpr, this.quality.dprCap);
    const targetW = Math.round(width * dpr);
    const targetH = Math.round(height * dpr);
    if (
      this.canvas.width !== targetW ||
      this.canvas.height !== targetH ||
      this.lastDpr !== dpr
    ) {
      this.canvas.width = targetW;
      this.canvas.height = targetH;
      this.lastDpr = dpr;
    }
    this.ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
  }

  /**
   * Public accessor used by the session to position score-pop flyers
   * at the HUD score readout's approximate location, so pops always
   * arc toward where the score lives. Mirrors the layout used by the
   * HUD (anchored to the playing field's right edge, not the
   * canvas's) so flyers home in on the actual readout regardless of
   * how wide the canvas is.
   */
  getScoreHudTarget(canvasWidth: number, canvasHeight: number): { x: number; y: number } {
    const layout = computeLayout(canvasWidth, canvasHeight);
    return {
      x: layout.lane0X + layout.laneStackWidth,
      y: SCORE_HUD_TARGET_TOP_PAD_PX,
    };
  }

  /**
   * Centre point of the named lane at the hit line, in canvas CSS
   * px. Session-side FX (particles, shockwaves, score pops) spawn
   * from this point so the eye is anchored to the lane that just
   * fired. Renderer-owned to keep the lane-geometry math in one
   * place — the session should never recompute lane geometry.
   */
  getLaneHitPoint(
    lane: ManiaLane,
    canvasWidth: number,
    canvasHeight: number,
  ): { x: number; y: number } {
    const layout = computeLayout(canvasWidth, canvasHeight);
    const x = layout.lane0X + lane * (layout.laneWidth + LANE_GAP_PX) + layout.laneWidth / 2;
    return { x, y: layout.hitLineY };
  }
}
